import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, Link } from 'wouter';
import { User, ArrowRight, RefreshCw } from 'lucide-react';
import { PaywallHeader } from '../components/paywall/PaywallHeader';
import { PartialPreview } from '../components/paywall/PartialPreview';
import { ValueList } from '../components/paywall/ValueList';
import { PaywallCTA } from '../components/paywall/PaywallCTA';
import { LoadingState } from '../components/recommendations/LoadingState';
import { QuestionnaireSessionService, SessionSaveError } from '../services/questionnaireSession';
import { getRecommendations } from '../utils/recommendations';
import { logger } from '../utils/logger';
import { SEO } from '../components/SEO';
import Logo from '../components/Logo';
import { STRIPE_PRODUCTS, formatPrice } from '../stripe-config';
import type { Recommendation, QuestionnaireData } from '../types';

export function PaywallPage() {
  const [, setLocation] = useLocation();
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [userName, setUserName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorType, setErrorType] = useState<'session_not_found' | 'no_recommendations' | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const loadSessionData = useCallback(async (useRetry: boolean = false) => {
    try {
      setIsLoading(true);
      setHasError(false);

      const session = useRetry
        ? await QuestionnaireSessionService.getSessionWithRetry()
        : await QuestionnaireSessionService.getSession();

      if (!session || !session.form_data) {
        logger.warn('No session data found after fetch');
        setErrorType('session_not_found');
        setHasError(true);
        setIsLoading(false);
        return;
      }

      const formData = session.form_data as QuestionnaireData;
      setUserName(session.name || (formData as any).name || '');

      const recs = getRecommendations(formData);
      if (recs.length === 0) {
        logger.error('No recommendations generated from form data', { formData });
        setErrorType('no_recommendations');
        setHasError(true);
        setIsLoading(false);
        return;
      }
      setRecommendations(recs.slice(0, 4));
      setIsLoading(false);
    } catch (error) {
      logger.error('Error loading session data', error);
      setErrorType('session_not_found');
      setHasError(true);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessionData(true);
  }, [loadSessionData]);

  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    await loadSessionData(true);
    setIsRetrying(false);
  }, [loadSessionData]);

  async function handleUnlock() {
    try {
      setIsCheckoutLoading(true);

      const sessionId = QuestionnaireSessionService.getSessionId();
      const session = await QuestionnaireSessionService.getSession(sessionId);

      if (!session || !session.form_data) {
        setIsCheckoutLoading(false);
        alert('Unable to retrieve your session data. Please try again or start a new questionnaire.');
        return;
      }

      try {
        await QuestionnaireSessionService.saveSessionMandatory({
          formData: session.form_data,
          currentStep: session.current_step || 8,
          name: userName || session.name || undefined,
        });
      } catch (saveError) {
        logger.error('Failed to save session before checkout:', saveError);
        setIsCheckoutLoading(false);

        if (saveError instanceof SessionSaveError) {
          alert(saveError.message);
        } else {
          alert('Unable to save your session. Please check your internet connection and try again.');
        }
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            price_id: STRIPE_PRODUCTS[0].priceId,
            mode: STRIPE_PRODUCTS[0].mode,
            success_url: `${window.location.origin}/success?session_id=${sessionId}`,
            cancel_url: `${window.location.origin}/unlock`,
            client_reference_id: sessionId,
            customer_name: userName || undefined,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        logger.error('Checkout session creation failed:', error);
        setIsCheckoutLoading(false);
        alert(error.error || 'Failed to create checkout session. Please try again.');
        return;
      }

      const checkoutSession = await response.json();
      window.location.href = checkoutSession.url;
    } catch (error) {
      logger.error('Error during checkout:', error);
      setIsCheckoutLoading(false);
      alert('There was an error processing your request. Please try again.');
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <LoadingState />
      </div>
    );
  }

  if (hasError) {
    const isNoRecommendations = errorType === 'no_recommendations';
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-lg mx-auto px-4 py-12 sm:py-16 text-center">
          <Logo className="mx-auto mb-8" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {isNoRecommendations ? 'Unable to Generate Recommendations' : 'Session Not Found'}
          </h1>
          <p className="text-gray-600 mb-6">
            {isNoRecommendations
              ? 'We had trouble generating recommendations based on your responses. This can happen with unusual combinations. Please try retaking the questionnaire with different selections.'
              : 'We couldn\'t find your questionnaire data. This can happen if you cleared your browser data or are using a different device.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {!isNoRecommendations && (
              <button
                onClick={handleRetry}
                disabled={isRetrying}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${isRetrying ? 'animate-spin' : ''}`} />
                {isRetrying ? 'Retrying...' : 'Try Again'}
              </button>
            )}
            <Link
              href="/questionnaire"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors"
            >
              {isNoRecommendations ? 'Retake Questionnaire' : 'Start New Questionnaire'}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SEO
        title="Unlock Your Business Software Blueprint | btoolme"
        description={`Get your personalized software recommendations. Unlock your complete Business Software Blueprint for ${formatPrice(STRIPE_PRODUCTS[0].price, STRIPE_PRODUCTS[0].currency)}.`}
        path="/unlock"
        noindex={true}
        faqItems={[
          {
            question: "What's included in my Business Software Blueprint?",
            answer: "Your Blueprint includes a Core Stack (the essential tools you need right now), a Growth Stack (tools to add as you scale), an Avoid List (tools to skip so you don't overpay), and plain-English rationale for every recommendation."
          },
          {
            question: "Is there a money-back guarantee?",
            answer: "Yes. If you don't feel 100% confident in your software decisions within 7 days of purchase, we'll refund every penny — no questions asked."
          },
          {
            question: "How do I access my Blueprint after purchase?",
            answer: "Immediately after payment you're redirected back to view your full Blueprint. It's also accessible whenever you return to btoolme from the same device."
          },
          {
            question: "Is this a subscription?",
            answer: "No. This is a one-time payment. You pay once and own your Blueprint forever — no recurring charges, no hidden fees."
          }
        ]}
      />
      <div className="max-w-lg mx-auto px-4 py-12 sm:py-16">
        <div className="space-y-8">
          <PaywallHeader userName={userName} />

          <PartialPreview recommendations={recommendations} />

          <ValueList />

          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Your Name (Optional)
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="name"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-gray-900"
                placeholder="John Smith"
              />
            </div>
            <p className="mt-1.5 text-xs text-gray-500">
              Used to personalize your Blueprint
            </p>
          </div>

          <PaywallCTA onUnlock={handleUnlock} isLoading={isCheckoutLoading} />
        </div>
      </div>
    </div>
  );
}
